import { Typography, useTheme } from '@material-ui/core'
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts'

import { PromotionSkipCount, SkipReasonCount } from '../../graphql/promotions'
import { formatCount } from '../../helpers/promotionAmount'
import { makeTruncatedCategoryTick } from '../../helpers/rechartsAxisTick'
import { getDirection, getTranslate } from '../../localization'
import {
  promotionBars,
  reasonBars,
  SkipBar,
  worthCharting
} from './skipChartSeries'
import useStyles from './styles/charts.styles'

/**
 * The skip screen's charts.
 *
 * NO PER-CURRENCY RULE HERE, unlike the Overview's money charts, and the reason is worth
 * stating so nobody adds one by analogy: everything on this screen is a COUNT. Counts are
 * dimensionless, so a single chart may span promotions and reasons freely. The Overview
 * splits by currency because minor-unit scale differs between them; nothing here has a unit.
 *
 * Every tick and tooltip routes through formatCount. Recharts generates labels itself, in
 * Latin digits, with no knowledge of the locale — left alone it would put Latin numerals on a
 * Persian screen beside tables showing Persian ones.
 *
 * ACCESSIBILITY: aria-hidden, deliberately. Recharts emits SVG with no structure a screen
 * reader can use, and the two tables below carry the same figures with real semantics — the
 * by-reason table row for row, and the by-promotion table in more detail than the chart, which
 * sums across reasons. The tables are the accessible presentation; these are a second view
 * for people who read shapes faster than columns.
 */
interface Props {
  byReason: SkipReasonCount[]
  byPromotion: PromotionSkipCount[]
}

const SkipCharts = ({ byReason, byPromotion }: Props) => {
  const classes = useStyles()
  const theme = useTheme()
  const isRtl = getDirection() === 'rtl'

  const reasons = reasonBars(byReason)
  const promotions = promotionBars(byPromotion)

  const chart = (
    bars: SkipBar[],
    id: string,
    title: string,
    dataCy: string
  ) => (
    <section aria-labelledby={id}>
      <Typography
        variant="h6"
        component="h2"
        id={id}
        className={classes.heading}>
        {getTranslate(title)}
      </Typography>
      <div className={classes.chart} aria-hidden="true" data-cy={dataCy}>
        <ResponsiveContainer width="100%" height={40 * bars.length + 60}>
          <BarChart data={bars} layout="vertical" barCategoryGap={8}>
            <CartesianGrid strokeDasharray="3 3" horizontal={false} />
            {/* reversed in RTL so bars grow from the right, where a Persian reader's eye
                starts. Recharts has no RTL mode; this is the axis-level equivalent. */}
            <XAxis
              type="number"
              reversed={isRtl}
              tickFormatter={(value: number) => formatCount(value)}
              allowDecimals={false}
            />
            <YAxis
              type="category"
              dataKey="label"
              width={260}
              orientation={isRtl ? 'right' : 'left'}
              tick={makeTruncatedCategoryTick(260, getTranslate)}
            />
            <Tooltip
              formatter={(value: number) => formatCount(value)}
              labelFormatter={(label: string) => getTranslate(label)}
              contentStyle={{ direction: isRtl ? 'rtl' : 'ltr' }}
            />
            <Bar
              dataKey="count"
              fill={theme.palette.primary.main}
              radius={[0, 3, 3, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </section>
  )

  return (
    <div className={classes.root}>
      {worthCharting(reasons) &&
        chart(
          reasons,
          'skip-reason-chart-heading',
          'Skips by reason',
          'SkipReasonChart'
        )}
      {worthCharting(promotions) &&
        chart(
          promotions,
          'skip-promotion-chart-heading',
          'Skips by promotion',
          'SkipPromotionChart'
        )}
    </div>
  )
}

export default SkipCharts
